import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useMutation } from '@apollo/client/react'

import { useAccountId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { NoteContent } from './noteContent'

import { CollaborationNoteWithActionsFields } from './fragments/CollaborationNoteWithActionsFields'
import type { CreatedNoteEdge } from './notesCache'
import { notesInput } from './notesQueries'
import type { CreatedNote } from './notesQueries'
import { makeNoteSubject, noteSubjectKey } from './noteSubject'
import type { NoteSubject } from './noteSubject'

export const CreateCollaborationNoteMutation = graphql(
  `
    mutation CreateCollaborationNote($input: CreateCollaborationNoteInput!) {
      createCollaborationNote(input: $input) {
        __typename
        ... on CreateCollaborationNoteSuccess {
          edge {
            cursor
            node {
              ...CollaborationNoteWithActionsFields
            }
          }
        }
        ... on InvalidInputError {
          message
        }
        ... on LimitReachedError {
          message
        }
        ... on CollaborationNoteThreadClosedError {
          message
        }
      }
    }
  `,
  [CollaborationNoteWithActionsFields],
)

export type CreateNoteFailure =
  | 'validation'
  | 'limit'
  | 'permission'
  | 'request'
  /** The thread stopped taking new Notes, as when a draft was scheduled. */
  | 'closed'
/**
 * On success, the new Note's edge from the create response, with its cursor
 * and the account's allowed actions, ready to insert into the thread.
 */
export type CreateNoteResult =
  | { ok: true; edge: CreatedNoteEdge }
  | { ok: false; reason: CreateNoteFailure }

export function useCreateNote(
  subject: NoteSubject,
  allowed: boolean,
): {
  submit: (content: NoteContent) => Promise<CreateNoteResult>
  pending: boolean
  error: CreateNoteFailure | null
  failRequest: () => void
  confirmedNotes: readonly CreatedNote[]
  retireConfirmedNotes: (ids: readonly string[]) => void
  isCurrentSelection: () => boolean
} {
  const accountId = useAccountId()
  const { subjectType, subjectId } = subject
  const selection = useMemo(
    () =>
      accountId
        ? { accountId, subject: makeNoteSubject(subjectType, subjectId) }
        : null,
    [accountId, subjectId, subjectType],
  )
  const selectionKey = selection
    ? noteSubjectKey(selection.accountId, selection.subject)
    : null
  const currentSelectionKey = useRef<string | null>(null)
  const selectionVersion = useRef(0)
  const completedVersion = useRef<number | null>(null)
  useLayoutEffect(() => {
    selectionVersion.current += 1
    currentSelectionKey.current = selectionKey
    return (): void => {
      currentSelectionKey.current = null
    }
  }, [selectionKey])
  const pendingKeysRef = useRef<Set<string>>(new Set())
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set())
  const [errors, setErrors] = useState<Record<string, CreateNoteFailure>>({})
  const [confirmed, setConfirmed] = useState<Record<string, CreatedNote[]>>({})
  const [mutate] = useMutation(CreateCollaborationNoteMutation, {
    errorPolicy: 'all',
  })

  const submit = useCallback(
    async ({ text, annotations }: NoteContent): Promise<CreateNoteResult> => {
      const submittedVersion = selectionVersion.current
      if (!selection || !selectionKey || !allowed) {
        return { ok: false, reason: 'permission' }
      }
      if (!text.trim() || text.length > 500) {
        setErrors((current) => ({ ...current, [selectionKey]: 'validation' }))
        return { ok: false, reason: 'validation' }
      }
      if (pendingKeysRef.current.has(selectionKey)) {
        return { ok: false, reason: 'request' }
      }

      pendingKeysRef.current.add(selectionKey)
      setPendingKeys((current) => new Set(current).add(selectionKey))
      setErrors((current) => {
        if (!(selectionKey in current)) return current
        const next = { ...current }
        delete next[selectionKey]
        return next
      })
      let reason: CreateNoteFailure | null = null
      try {
        const response = await mutate({
          variables: {
            input: { ...notesInput(selection.subject), text, annotations },
          },
          fetchPolicy: 'no-cache',
        })
        const payload = response.data?.createCollaborationNote
        if (payload?.__typename === 'CreateCollaborationNoteSuccess') {
          completedVersion.current = submittedVersion
          if (currentSelectionKey.current !== null) {
            setConfirmed((current) => ({
              ...current,
              [selectionKey]: [
                payload.edge.node,
                ...(current[selectionKey] ?? []).filter(
                  (note) => note.id !== payload.edge.node.id,
                ),
              ],
            }))
          }
          return { ok: true, edge: payload.edge }
        }
        if (response.error) {
          reason =
            CombinedGraphQLErrors.is(response.error) &&
            response.error.errors.some(
              (error) => error.extensions?.code === 'FORBIDDEN',
            )
              ? 'permission'
              : 'request'
        } else {
          reason =
            payload?.__typename === 'InvalidInputError'
              ? 'validation'
              : payload?.__typename === 'LimitReachedError'
                ? 'limit'
                : payload?.__typename === 'CollaborationNoteThreadClosedError'
                  ? 'closed'
                  : 'request'
        }
      } catch {
        reason = 'request'
      } finally {
        pendingKeysRef.current.delete(selectionKey)
        setPendingKeys((current) => {
          const next = new Set(current)
          next.delete(selectionKey)
          return next
        })
      }

      setErrors((current) => ({
        ...current,
        [selectionKey]: reason ?? 'request',
      }))
      return { ok: false, reason: reason ?? 'request' }
    },
    [allowed, mutate, selection, selectionKey],
  )

  const retireConfirmedNotes = useCallback(
    (ids: readonly string[]): void => {
      if (
        !selectionKey ||
        !confirmed[selectionKey]?.some((note) => ids.includes(note.id))
      )
        return
      setConfirmed((current) => {
        const remaining = (current[selectionKey] ?? []).filter(
          (note) => !ids.includes(note.id),
        )
        if (remaining.length === (current[selectionKey] ?? []).length) {
          return current
        }
        return { ...current, [selectionKey]: remaining }
      })
    },
    [confirmed, selectionKey],
  )

  const isCurrentSelection = useCallback(
    (): boolean =>
      currentSelectionKey.current === selectionKey &&
      completedVersion.current === selectionVersion.current,
    [selectionKey],
  )

  const failRequest = useCallback((): void => {
    if (!selectionKey) return
    setErrors((current) => ({ ...current, [selectionKey]: 'request' }))
  }, [selectionKey])

  return {
    submit,
    pending: selectionKey ? pendingKeys.has(selectionKey) : false,
    error: selectionKey ? (errors[selectionKey] ?? null) : null,
    failRequest,
    confirmedNotes: selectionKey ? (confirmed[selectionKey] ?? []) : [],
    retireConfirmedNotes,
    isCurrentSelection,
  }
}
